import React, { type RefObject, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import type { ConnectedComponentClass, SearchBoxExposed, SearchBoxProvided } from 'react-instantsearch-core';
import { connectSearchBox } from 'react-instantsearch-dom';

import SearchIcon from '@atlaskit/icon/core/search';
import { Box, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

const SEARCH_DEBOUNCE_MS = 350;

const searchWrapperStyles = xcss({
	flex: '1 1 auto',

	// @ts-expect-error hardcode input font size
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	input: {
		// Safari zooms if the input has smaller text
		// This needs to be wrapped with token() since xcss types only work for top level properties
		// eslint-disable-next-line @atlaskit/design-system/use-latest-xcss-syntax-typography -- Ignored via go/DSP-18766
		font: token('font.body.large'),
	},
});

const searchIconStyles = xcss({
	alignSelf: 'center',
});

type SearchInputProps = {
	testId?: string;
	onQueryChange?(value: string): void;
	onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
	inputRef?: RefObject<HTMLInputElement>;
	isExpanded?: boolean;
	listboxId?: string;
	activeDescendantId?: string;
	onRefineReady?: (refine: (query: string) => void) => void;
} & SearchBoxProvided;

/**
 * __Search input__
 */
const SearchInput: ConnectedComponentClass<SearchInputProps, SearchBoxProvided, SearchBoxExposed> = connectSearchBox<SearchInputProps>(
	({
		refine,
		testId,
		onQueryChange,
		onKeyDown,
		inputRef,
		isExpanded,
		listboxId,
		activeDescendantId,
		onRefineReady,
	}) => {
		const [inputValue, setInputValue] = useState('');

		// Expose refine function to parent
		React.useEffect(() => {
			onRefineReady?.(refine);
		}, [refine, onRefineReady]);

		const handleChange = useMemo(
			() =>
				debounce((inputValue: string) => {
					refine(inputValue);
				}, SEARCH_DEBOUNCE_MS),
			[refine],
		);

		return (
			<Box xcss={searchWrapperStyles}>
				<VisuallyHidden>
					<label htmlFor="search-input">Search</label>
				</VisuallyHidden>
				<Textfield
					testId={testId ? `${testId}-search-input` : undefined}
					id="search-input"
					ref={inputRef}
					role="combobox"
					autoComplete="off"
					aria-autocomplete="list"
					aria-haspopup="listbox"
					aria-expanded={isExpanded}
					aria-controls={listboxId}
					aria-activedescendant={activeDescendantId}
					onKeyDown={onKeyDown}
					onChange={(e) => {
						const nextValue = e.currentTarget.value;
						setInputValue(nextValue);
						onQueryChange?.(nextValue);
						handleChange(nextValue);
					}}
					appearance="none"
					placeholder="Search the Atlassian Design System"
					value={inputValue}
					elemBeforeInput={
						<Box as="span" paddingInlineEnd="space.100" xcss={searchIconStyles}>
							<SearchIcon spacing="spacious" label="" color={token('color.icon')} />
						</Box>
					}
				/>
			</Box>
		);
	},
);

export default SearchInput;
